import { CINEMA_CONTACTS_LIST } from "../data/cinemaContacts.generated"
import { cinemaHref, getCinemaSlugMeta, parseCinemaPath, slugForCinemaId } from "./cinemaSlugs"

export const SITE_NAME = "Мягкий кинотеатр"
export const SITE_ORIGIN = "https://мягкийкинотеатр.рф"
export const DEFAULT_OG_IMAGE = `${SITE_ORIGIN}/icons/icon-512.png`

export const DEFAULT_DESCRIPTION =
    "Мягкий кинотеатр — сеть кинотеатров с роботизированными креслами, большим пространством и 4K Dolby. Афиша, билеты онлайн и адреса залов в Челябинске, Самаре, Уфе, Москве и других городах."

export type DocumentSeo = {
    title: string
    description: string
    path: string
    noindex?: boolean
    image?: string
}

function brand(page: string): string {
    return `${page} — ${SITE_NAME}`
}

const PAGE_SEO: Record<string, Omit<DocumentSeo, "path">> = {
    "/": {
        title: SITE_NAME,
        description: DEFAULT_DESCRIPTION,
    },
    "/afisha": {
        title: brand("Афиша"),
        description:
            "Афиша «Мягкий кинотеатр»: расписание сеансов, фильмы в прокате и покупка билетов онлайн.",
    },
    "/contacts": {
        title: brand("Контакты"),
        description:
            "Адреса, телефоны и режим работы кинотеатров сети «Мягкий кинотеатр».",
    },
    "/location": {
        title: brand("Кинотеатры"),
        description:
            "Выберите кинотеатр сети «Мягкий кинотеатр» в Челябинске, Самаре, Уфе, Москве и других городах.",
    },
    "/profile/about": {
        title: brand("Справка"),
        description:
            "Что такое Мягкий кинотеатр: роботизированные кресла, QR-вход в зал, кинобонусы и Пушкинская карта.",
    },
    "/privacy": {
        title: brand("Политика конфиденциальности"),
        description: "Политика конфиденциальности сайта «Мягкий кинотеатр».",
    },
    "/personal-data": {
        title: brand("Обработка персональных данных"),
        description: "Сведения об обработке персональных данных в сети «Мягкий кинотеатр».",
    },
    "/terms": {
        title: brand("Пользовательское соглашение"),
        description: "Пользовательское соглашение сайта «Мягкий кинотеатр».",
    },
    "/purchase-terms": {
        title: brand("Условия покупки и возврата"),
        description: "Условия покупки и возврата билетов в сети «Мягкий кинотеатр».",
    },
    "/auth": {
        title: brand("Вход"),
        description: DEFAULT_DESCRIPTION,
        noindex: true,
    },
    "/buy": {
        title: brand("Покупка билета"),
        description: DEFAULT_DESCRIPTION,
        noindex: true,
    },
    "/tickets": {
        title: brand("Мои билеты"),
        description: DEFAULT_DESCRIPTION,
        noindex: true,
    },
    "/profile": {
        title: brand("Профиль"),
        description: DEFAULT_DESCRIPTION,
        noindex: true,
    },
}

const NOINDEX_PREFIXES = [
    "/profile/edit",
    "/profile/notifications",
    "/profile/favourites",
    "/profile/history",
    "/profile/promocode",
    "/profile/support",
]

export function excerpt(text: string | null | undefined, max = 180): string {
    const clean = (text ?? "").replace(/\s+/g, " ").trim()
    if (clean.length <= max) return clean
    const sliced = clean.slice(0, max)
    const lastSpace = sliced.lastIndexOf(" ")
    return `${(lastSpace > 80 ? sliced.slice(0, lastSpace) : sliced).trim()}…`
}

export function getSeoForPath(pathname: string): DocumentSeo {
    const { slug, rest } = parseCinemaPath(pathname)
    const cinemaMeta = slug ? getCinemaSlugMeta(slug) : undefined
    const place = cinemaMeta ? cinemaMeta.seoName : SITE_NAME
    const logical = slug ? rest : pathname

    if (cinemaMeta && logical === "/afisha") {
        return {
            title: `Афиша — ${place}`,
            description: `Афиша ${place} (${cinemaMeta.cityName}): расписание сеансов, фильмы в прокате и билеты онлайн.`,
            path: pathname,
        }
    }

    if (cinemaMeta && logical === "/contacts") {
        return {
            title: `Контакты — ${place}`,
            description: `Адрес, телефон и режим работы: ${place}, ${cinemaMeta.cityName}.`,
            path: pathname,
        }
    }

    const exact = PAGE_SEO[logical] ?? PAGE_SEO[pathname]
    if (exact) return { ...exact, path: pathname }

    if (/^\/afisha\/film\/\d+$/.test(logical)) {
        return {
            title: cinemaMeta ? `Фильм — ${place}` : brand("Фильм"),
            description: `Описание фильма, расписание сеансов и покупка билетов в «${place}».`,
            path: pathname,
        }
    }

    if (/^\/promo\/[^/]+$/.test(logical)) {
        return {
            title: cinemaMeta ? `Акция — ${place}` : brand("Акция"),
            description: `Акции и спецпредложения «${place}»: скидки, кинобонусы и спецсеансы.`,
            path: pathname,
        }
    }

    if (NOINDEX_PREFIXES.some(prefix => pathname === prefix || pathname.startsWith(`${prefix}/`))) {
        return {
            title: SITE_NAME,
            description: DEFAULT_DESCRIPTION,
            path: pathname,
            noindex: true,
        }
    }

    return {
        title: SITE_NAME,
        description: DEFAULT_DESCRIPTION,
        path: pathname,
    }
}

function upsertMeta(attr: "name" | "property", key: string, content: string) {
    let el = document.head.querySelector(`meta[${attr}="${key}"]`) as HTMLMetaElement | null
    if (!el) {
        el = document.createElement("meta")
        el.setAttribute(attr, key)
        document.head.appendChild(el)
    }
    el.content = content
}

function upsertLink(rel: string, href: string) {
    let el = document.head.querySelector(`link[rel="${rel}"]`) as HTMLLinkElement | null
    if (!el) {
        el = document.createElement("link")
        el.rel = rel
        document.head.appendChild(el)
    }
    el.href = href
}

export function applyDocumentSeo(seo: DocumentSeo) {
    const url = `${SITE_ORIGIN}${seo.path === "/" ? "/" : seo.path}`
    const image = seo.image || DEFAULT_OG_IMAGE

    document.title = seo.title
    upsertMeta("name", "description", seo.description)
    upsertMeta("name", "robots", seo.noindex ? "noindex, nofollow" : "index, follow")
    upsertMeta("property", "og:title", seo.title)
    upsertMeta("property", "og:description", seo.description)
    upsertMeta("property", "og:url", url)
    upsertMeta("property", "og:image", image)
    upsertMeta("property", "og:type", "website")
    upsertMeta("property", "og:site_name", SITE_NAME)
    upsertMeta("property", "og:locale", "ru_RU")
    upsertLink("canonical", url)
}

function toTel(phone: string): string {
    const digits = phone.replace(/\D/g, "")
    if (digits.length === 11 && digits.startsWith("8")) return `+7${digits.slice(1)}`
    if (digits.length === 11 && digits.startsWith("7")) return `+${digits}`
    if (digits.length === 10) return `+7${digits}`
    return phone.startsWith("+") ? `+${digits}` : `+${digits}`
}

export function buildSeoJsonLd(): Record<string, unknown> {
    const theaters = CINEMA_CONTACTS_LIST.filter(cinema => cinema.seoName).map(cinema => {
        const city = cinema.city ?? ""
        const address = (cinema.address ?? "").replace(/\s*\n\s*/g, ", ")
        const node: Record<string, unknown> = {
            "@type": "MovieTheater",
            name: cinema.seoName,
            url: slugForCinemaId(cinema.cinemaId)
                ? `${SITE_ORIGIN}${cinemaHref(slugForCinemaId(cinema.cinemaId)!, "/contacts")}`
                : `${SITE_ORIGIN}/contacts`,
            address: {
                "@type": "PostalAddress",
                streetAddress: address || undefined,
                addressLocality: city || undefined,
                addressCountry: "RU",
            },
        }
        if (cinema.sessionsPhone) node.telephone = toTel(cinema.sessionsPhone)
        if (cinema.email) node.email = cinema.email
        if (cinema.latitude && cinema.longitude) {
            node.geo = {
                "@type": "GeoCoordinates",
                latitude: cinema.latitude,
                longitude: cinema.longitude,
            }
        }
        return node
    })

    return {
        "@context": "https://schema.org",
        "@graph": [
            {
                "@type": "Organization",
                "@id": `${SITE_ORIGIN}/#organization`,
                name: SITE_NAME,
                legalName: "ООО «ИМПЕРИЯ ИГР»",
                url: `${SITE_ORIGIN}/`,
                logo: DEFAULT_OG_IMAGE,
                email: "almaz@comfortkino.ru",
                telephone: "+73512101111",
                address: {
                    "@type": "PostalAddress",
                    streetAddress: "Свердловский проспект, 51А",
                    addressLocality: "Челябинск",
                    postalCode: "454008",
                    addressCountry: "RU",
                },
                department: theaters,
            },
            {
                "@type": "WebSite",
                "@id": `${SITE_ORIGIN}/#website`,
                url: `${SITE_ORIGIN}/`,
                name: SITE_NAME,
                description: DEFAULT_DESCRIPTION,
                inLanguage: "ru-RU",
                publisher: { "@id": `${SITE_ORIGIN}/#organization` },
            },
        ],
    }
}

function filmDurationIso(raw?: string): string | undefined {
    if (!raw) return undefined
    const hours = Number(raw.match(/(\d+)\s*ч/i)?.[1] ?? 0)
    const mins = Number(raw.match(/(\d+)\s*мин/i)?.[1] ?? 0)
    if (hours && mins) return `PT${hours}H${mins}M`
    if (hours) return `PT${hours}H`
    if (mins) return `PT${mins}M`
    const only = raw.match(/(\d+)/)
    return only ? `PT${only[1]}M` : undefined
}

export function buildMovieJsonLd(
    film: {
        id: number
        name: string
        description?: string
        poster?: { big?: string }
        duration?: string
        genres?: string
        directors?: string
        actors?: string
        countries?: string
        age?: string
    },
    cinemaSlug?: string,
): Record<string, unknown> {
    const path = cinemaSlug ? cinemaHref(cinemaSlug, `/afisha/film/${film.id}`) : `/afisha/film/${film.id}`
    const url = `${SITE_ORIGIN}${path}`
    const node: Record<string, unknown> = {
        "@context": "https://schema.org",
        "@type": "Movie",
        name: film.name,
        url,
        inLanguage: "ru",
        publisher: { "@id": `${SITE_ORIGIN}/#organization` },
    }
    if (film.description) node.description = excerpt(film.description, 400)
    if (film.poster?.big) node.image = film.poster.big
    const duration = filmDurationIso(film.duration)
    if (duration) node.duration = duration
    if (film.genres) {
        node.genre = film.genres.split(/[,;]/).map(part => part.trim()).filter(Boolean)
    }
    if (film.directors) {
        node.director = film.directors.split(/[,;]/).map(name => ({
            "@type": "Person",
            name: name.trim(),
        })).filter(person => person.name)
    }
    if (film.actors) {
        node.actor = film.actors.split(/[,;]/).map(name => ({
            "@type": "Person",
            name: name.trim(),
        })).filter(person => person.name)
    }
    if (film.countries) node.countryOfOrigin = film.countries
    if (film.age) node.contentRating = film.age
    return node
}
